<script lang="ts">
import { defineComponent } from "vue";

import aiCoachMarkDecor from "@/assets/images/ai-coach-mark-decor.svg";
import aiIconDefault from "@/assets/images/ai-prompt-default-v2.svg?raw";
import { Statsig } from "@/env/statsig/client";
import {
  fetchUserActions,
  updateUserActions,
} from "@/services/profileService/userActions";
import { EventBus } from "@/store/eventBus";
import { sendButtonCtaTelemetry } from "@/utils/ctaTelemetry";
import isUnauth from "@/utils/isUnauth";

const COACH_MARK_STORAGE_KEY = "aiCoachMarkDisplayCount";
const COACH_MARK_ELEMENT = "aiCoachMark";
const COACH_MARK_GUID = "ai-coach-mark";
const COACH_MARK_MAX_DISPLAYS = 2;
const VALID_PROMPT_STATES = new Set(["default", "processing", "responding"]);

const readDisplayCount = () => {
  const rawCount = Number(localStorage.getItem(COACH_MARK_STORAGE_KEY));
  return Number.isFinite(rawCount) ? rawCount : 0;
};

const writeDisplayCount = (count: number) => {
  localStorage.setItem(COACH_MARK_STORAGE_KEY, String(count));
};

const clearDisplayCount = () => {
  localStorage.removeItem(COACH_MARK_STORAGE_KEY);
};

export default defineComponent({
  name: "AiPromptBtn",
  props: {
    dark: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      defaultState: "default" as const,
      currentState: "default",
      aiIconDefault,
      aiCoachMarkDecor,
      eligible: false,
      acknowledged: false,
      hovering: false,
      displayCount: readDisplayCount(),
      acknowledgementStatus: "idle" as "idle" | "syncing" | "synced",
      onClickOutside: null as ((e: MouseEvent) => void) | null,
      enableAiLearningExperience: false,
    };
  },
  async mounted() {
    EventBus.on("updatePromptBtnState", (promptState: string) => {
      if (VALID_PROMPT_STATES.has(promptState)) {
        this.currentState = promptState;
      }
    });
    this.enableAiLearningExperience = await Statsig.featureGate(
      "ENABLE_AI_LEARNING_EXPERIENCE"
    );
    if (this.enableAiLearningExperience) {
      await this.bootstrapCoachMark();
    }
  },
  beforeUnmount() {
    this.removeClickOutsideListener();
  },
  computed: {
    isUnauth,
    showCoachMark(): boolean {
      return (
        this.enableAiLearningExperience &&
        this.eligible &&
        this.hovering &&
        !this.acknowledged
      );
    },
  },
  methods: {
    onMouseEnter() {
      if (this.hovering) return;
      this.hovering = true;
      this.trackCoachMarkDisplay();
    },
    trackCoachMarkDisplay() {
      if (!this.eligible || this.acknowledged) return;

      this.displayCount++;
      writeDisplayCount(this.displayCount);
      this.$nextTick(() => this.addClickOutsideListener());
    },
    addClickOutsideListener() {
      this.removeClickOutsideListener();
      this.onClickOutside = (e: MouseEvent) => {
        const el = this.$el as HTMLElement;
        if (el && !el.contains(e.target as Node)) {
          this.dismissCoachMark();
        }
      };
      document.addEventListener("click", this.onClickOutside, true);
    },
    removeClickOutsideListener() {
      if (this.onClickOutside) {
        document.removeEventListener("click", this.onClickOutside, true);
        this.onClickOutside = null;
      }
    },
    hideCoachMark() {
      this.hovering = false;
      this.removeClickOutsideListener();
    },
    dismissCoachMark() {
      this.hideCoachMark();
      if (this.displayCount >= COACH_MARK_MAX_DISPLAYS && !this.acknowledged) {
        this.eligible = false;
      }
    },
    acknowledgeCoachMarkLocally() {
      this.acknowledged = true;
      this.eligible = false;
      this.dismissCoachMark();
    },
    openAiPrompt() {
      if (this.currentState !== this.defaultState) return;

      if (this.enableAiLearningExperience) {
        this.acknowledgeCoachMarkLocally();
        this.sendAcknowledgement();
      }
      globalThis.location.href = "/ai-assistant";
    },

    async bootstrapCoachMark() {
      if (this.isUnauth) return;

      try {
        const res = await fetchUserActions();
        const hasAcknowledgement = Boolean(
          res.actions?.some(
            (action: { element?: string }) =>
              action.element === COACH_MARK_ELEMENT
          )
        );

        this.acknowledged = hasAcknowledgement;
        this.eligible =
          !hasAcknowledgement && this.displayCount < COACH_MARK_MAX_DISPLAYS;
        this.acknowledgementStatus = hasAcknowledgement ? "synced" : "idle";
      } catch {
        // silently fail
      }
    },

    async onGotItClick() {
      this.acknowledgeCoachMarkLocally();
      await this.sendAcknowledgement();
    },

    async sendAcknowledgement() {
      if (this.acknowledgementStatus !== "idle") return;

      this.acknowledgementStatus = "syncing";

      try {
        await updateUserActions({
          action: "Clicked",
          element: COACH_MARK_ELEMENT,
          guid: COACH_MARK_GUID,
        });

        await sendButtonCtaTelemetry(
          this.$route.fullPath,
          "ai-coach-mark-got-it",
          this.isUnauth
        );

        this.acknowledgementStatus = "synced";
        clearDisplayCount();
      } catch {
        this.acknowledgementStatus = "idle";
        // silently fail
      }
    },
  },
});
</script>

<template>
  <div class="relative" @mouseenter="onMouseEnter" @focusin="onMouseEnter">
    <button
      data-cy="ai-prompt-btn"
      class="flex items-center gap-2 p-[0.375rem] rounded-[999px] bg-transparent"
      :class="[
        enableAiLearningExperience
          ? {}
          : {
              'xlg:py-3 xlg:pr-4 xlg:pl-3': true,
              'hover:bg-white-dark active:bg-white-lightest focus:bg-white-dark text-white-lightest hover:text-black active:text-black focus:text-black':
                dark,
              'hover:bg-[#e2e2e2] active:bg-black focus:bg-[#e2e2e2] text-black active:text-white-lightest':
                !dark,
            },
      ]"
      @click="openAiPrompt"
      @keyup.enter="openAiPrompt"
      @keyup.space="openAiPrompt"
      tabindex="0"
    >
      <span
        class="w-8 h-8 xlg:w-6 xlg:h-6"
        :class="{
          'text-[#00BCEB]': dark,
          'text-[#0051AF]': !dark,
        }"
        v-html="aiIconDefault"
      />
      <span
        v-if="!enableAiLearningExperience"
        class="hidden xlg:block font-light"
        >AI Learning Experience</span
      >
    </button>
    <transition name="coach-mark">
      <div
        v-if="showCoachMark"
        class="absolute top-full left-1/2 z-[1000] w-[21.25rem]"
      >
        <div
          class="-ml-[0.625rem] w-0 h-0 border-l-[0.625rem] border-l-transparent border-r-[0.625rem] border-r-transparent border-b-[0.625rem] border-b-[#0051AF]"
        ></div>
        <div
          class="relative bg-[#0051AF] rounded p-6 shadow-lg overflow-hidden whitespace-normal -translate-x-[calc(100%-3rem)]"
          data-cy="ai-coach-mark"
        >
          <div class="text-white-lightest">
            <h2 class="text-[1.375rem] font-light leading-tight mb-3">
              Try our AI Learning Experience (Beta)
            </h2>
            <p class="text-[0.8125rem] leading-tight mb-5">
              Get personalized guidance with the AI Learning Experience included
              with Cisco U. All Access. Create a study plan, prep for your next
              cert, take a quiz, and more.
            </p>

            <button
              class="w-[8.6875rem] h-[2.3125rem] bg-[#FFFFFF] text-[#0D0D0E] border border-[#0D0D0E] rounded-full text-sm cursor-pointer flex items-center justify-center"
              data-cy="ai-coach-mark-got-it"
              tabindex="0"
              @click.stop="onGotItClick"
              @keyup.enter.stop="onGotItClick"
              @keyup.space.stop="onGotItClick"
            >
              Got it
            </button>
          </div>

          <img
            :src="aiCoachMarkDecor"
            class="absolute bottom-6 right-4"
            width="48"
            height="35"
            alt=""
            aria-hidden="true"
          />
        </div>
      </div>
    </transition>
  </div>
</template>

<style scoped>
.coach-mark-enter-active,
.coach-mark-leave-active {
  transition:
    opacity 0.2s ease,
    transform 0.2s ease;
}

.coach-mark-enter-from,
.coach-mark-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}
</style>
